import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { fetchData } from '../../../apiService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, Select, MenuItem, Divider,
  useMediaQuery, useTheme, Chip, Autocomplete, CircularProgress
} from '@mui/material';
import { Close, Add, Delete, Warning } from '@mui/icons-material';

const SchemeModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  schemeDetails,
  setSchemeDetails,
  onSave,
  items,
  brands,
  categories,
  loading,
  companyId,
  branchId,
  token,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Scheme'
    : modalAction === 'edit' ? 'Edit Scheme' : 'View Scheme';

  // Data loss warning
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [originalDetails, setOriginalDetails] = useState([]);

  // Suppliers (vendor list)
  const [suppliers, setSuppliers] = useState([]);
  const [customerFound, setCustomerFound] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Per-row dynamic item lists: { [rowIndex]: [...items] }
  const [rowItems, setRowItems] = useState({});
  const [rowItemsLoading, setRowItemsLoading] = useState({});
  const [detailError, setDetailError] = useState('');

  const fetchRowItems = useCallback((index, brandId, categoryId) => {
    if (!brandId && !categoryId) {
      setRowItems(prev => ({ ...prev, [index]: [] }));
      return;
    }
    const bId = brandId || 0;
    const cId = categoryId || 0;
    setRowItemsLoading(prev => ({ ...prev, [index]: true }));
    fetchData('SchemeApi', `/BrandAndCategoryWiseItemList/${companyId}/${branchId}/${bId}/${cId}`, token)
      .then(res => {
        const data = res.response_data || res || [];
        setRowItems(prev => ({ ...prev, [index]: data }));
      })
      .catch(() => setRowItems(prev => ({ ...prev, [index]: [] })))
      .finally(() => setRowItemsLoading(prev => ({ ...prev, [index]: false })));
  }, [companyId, branchId, token]);

  // Fetch suppliers when modal opens
  useEffect(() => {
    if (open && companyId && token) {
      fetchData('SchemeApi', `/Party/${companyId}/${branchId}`, token)
        .then(res => {
          const data = res.response_data || res || [];
          if (data.length > 0) console.log('🔍 Supplier fields:', Object.keys(data[0]), data[0]);
          setSuppliers(data);
        })
        .catch(() => setSuppliers([]));
    }
  }, [open, companyId, branchId, token]);

  useEffect(() => {
    if (open) {
      setOriginalFormData({ ...formData });
      setOriginalDetails(JSON.parse(JSON.stringify(schemeDetails)));
      setHasUnsavedChanges(false);
      setSelectedSupplier(null);
      setCustomerFound(false);
      setRowItems({});
      setRowItemsLoading({});
      setDetailError('');

      // Edit/View mode — pre-fetch item list for existing rows that have BrandId or CategoryId
      if ((modalAction === 'edit' || modalAction === 'view') && schemeDetails.length > 0) {
        // Filter out blank rows (no item selected) in edit mode
        if (modalAction === 'edit') {
          const validDetails = schemeDetails.filter(d => d.ItemId);
          if (validDetails.length !== schemeDetails.length) {
            setSchemeDetails(validDetails);
          }
        }
        const detailsToProcess = modalAction === 'edit' ? schemeDetails.filter(d => d.ItemId) : schemeDetails;
        detailsToProcess.forEach((detail, index) => {
          if (detail.BrandId || detail.CategoryId) {
            const bId = detail.BrandId || 0;
            const cId = detail.CategoryId || 0;
            setRowItemsLoading(prev => ({ ...prev, [index]: true }));
            fetchData('SchemeApi', `/BrandAndCategoryWiseItemList/${companyId}/${branchId}/${bId}/${cId}`, token)
              .then(res => {
                const data = res.response_data || res || [];
                setRowItems(prev => ({ ...prev, [index]: data }));
              })
              .catch(() => setRowItems(prev => ({ ...prev, [index]: [] })))
              .finally(() => setRowItemsLoading(prev => ({ ...prev, [index]: false })));
          }
        });
      }
    }
  }, [open]);

  // Track changes
  const changeTrackRef = useRef(null);
  useEffect(() => {
    if (!isViewMode && open && Object.keys(originalFormData).length > 0) {
      clearTimeout(changeTrackRef.current);
      changeTrackRef.current = setTimeout(() => {
        const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        const hasDetailChanges = JSON.stringify(schemeDetails) !== JSON.stringify(originalDetails);
        setHasUnsavedChanges(hasFormChanges || hasDetailChanges);
      }, 300);
    }
    return () => clearTimeout(changeTrackRef.current);
  }, [formData, schemeDetails, originalFormData, originalDetails, isViewMode, open]);

  const handleCloseClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectSupplier = (event, supplier) => {
    if (supplier) {
      // Build address from available fields
      const addressParts = [
        supplier.AddressLine1 || supplier.Address1 || supplier.Address || '',
        supplier.AddressLine2 || supplier.Address2 || '',
        supplier.Location || supplier.City || '',
        supplier.Pin || supplier.Pincode || supplier.PostalCode || ''
      ].filter(Boolean);
      const fullAddress = addressParts.join(', ');

      setFormData(prev => ({
        ...prev,
        SupplierName: supplier.Name || supplier.SupplierName || '',
        ContactNumber: supplier.ContactNumber1 || supplier.ContactNumber || supplier.Mobile || '',
        Address: fullAddress,
        MasterId: supplier.MasterId || supplier.Id || null
      }));
      setSelectedSupplier(supplier);
      setCustomerFound(true);
    } else {
      setFormData(prev => ({ ...prev, SupplierName: '', ContactNumber: '', Address: '', MasterId: null }));
      setSelectedSupplier(null);
      setCustomerFound(false);
    }
  };

  // Detail row handlers
  const handleDetailChange = useCallback((index, field, value) => {
    setDetailError('');
    const updatedDetails = [...schemeDetails];
    if (['Quantity', 'Rate'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        setSchemeDetails(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
    }
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    // If IsTarget changes, reset the other field
    if (field === 'IsTarget') {
      const targetVal = parseInt(value);
      if (targetVal === 1) updatedDetails[index].Rate = 0;
      else if (targetVal === 2) updatedDetails[index].Quantity = 0;
    }

    // If BrandId or CategoryId changes → reset ItemId and fetch new item list
    if (field === 'BrandId' || field === 'CategoryId') {
      updatedDetails[index].ItemId = null;
      const newBrandId = field === 'BrandId' ? value : updatedDetails[index].BrandId;
      const newCategoryId = field === 'CategoryId' ? value : updatedDetails[index].CategoryId;
      fetchRowItems(index, newBrandId, newCategoryId);
    }

    setSchemeDetails(updatedDetails);
  }, [schemeDetails, setSchemeDetails, fetchRowItems]);

  const addDetailRow = useCallback(() => {
    // Prevent adding new row if last row doesn't have an item selected
    if (schemeDetails.length > 0) {
      const lastRow = schemeDetails[schemeDetails.length - 1];
      if (!lastRow.ItemId) {
        setDetailError('Please select an item in the current row before adding a new row');
        return;
      }
      // Check qty is filled for Qty target type
      if (lastRow.IsTarget === 1 && (!lastRow.Quantity || parseFloat(lastRow.Quantity) <= 0)) {
        setDetailError('Please enter Quantity for the current row before adding a new row');
        return;
      }
      if (lastRow.IsTarget === 2 && (!lastRow.Rate || parseFloat(lastRow.Rate) <= 0)) {
        setDetailError('Please enter Rate for the current row before adding a new row');
        return;
      }
    }
    setDetailError('');
    setSchemeDetails(prev => [...prev, {
      Id: 0, BrandId: null, CategoryId: null, ItemId: null,
      IsTarget: 1, Quantity: 0, Rate: 0, Description: '', IsActive: true
    }]);
  }, [setSchemeDetails, schemeDetails]);

  const removeDetailRow = useCallback((index) => {
    setSchemeDetails(prev => prev.filter((_, i) => i !== index));
  }, [setSchemeDetails]);

  // Maps for display
  const itemNameMap = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.Id] = i.Code ? `${i.Code} - ${i.Name}` : i.Name; });
    return map;
  }, [items]);

  const brandNameMap = useMemo(() => {
    const map = {};
    brands.forEach(b => { map[b.Id] = b.Name; });
    return map;
  }, [brands]);

  const categoryNameMap = useMemo(() => {
    const map = {};
    categories.forEach(c => { map[c.Id] = c.Name; });
    return map;
  }, [categories]);

  const compactInput = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } })
  };
  const tinyInput = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.7rem', textAlign: 'right', fontWeight: 700 },
    minWidth: 0,
    ...(isViewMode && { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } })
  };

  // Table styles
  const thStyle = {
    padding: '8px 6px', textAlign: 'left', fontWeight: 600, color: '#e2e8f0',
    fontSize: '0.65rem', letterSpacing: '0.3px', textTransform: 'uppercase', whiteSpace: 'nowrap'
  };
  const tdStyle = { padding: '6px 6px', fontSize: '0.7rem', verticalAlign: 'middle' };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) { setCancelConfirmOpen(true); return; }
          }
          onClose();
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2 } }}
      >
        {/* Header */}
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                {title}
              </Typography>
              {hasUnsavedChanges && !isViewMode && (
                <Chip label="Unsaved" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }} />
              )}
            </Box>
            <IconButton size="small" onClick={handleCloseClick}><Close /></IconButton>
          </Box>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 2, overflow: 'auto' }}>
          {/* Scheme Info Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 1, display: 'block' }}>
              SCHEME DETAILS
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Scheme No" value={formData.SchemeNo || ''} disabled sx={compactInput} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Start Date" type="date" value={formData.SchemeStartDate || ''} disabled={isViewMode}
                  onChange={(e) => handleChange('SchemeStartDate', e.target.value)} sx={compactInput} InputLabelProps={{ shrink: true }}
                  error={!isViewMode && !!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeStartDate > formData.SchemeEndDate}
                  helperText={!isViewMode && !!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeStartDate > formData.SchemeEndDate ? 'Start Date cannot be after End Date' : ''} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="End Date *" type="date" value={formData.SchemeEndDate || ''} disabled={isViewMode}
                  onChange={(e) => handleChange('SchemeEndDate', e.target.value)} sx={compactInput} InputLabelProps={{ shrink: true }}
                  required
                  error={!isViewMode && (!formData.SchemeEndDate || (!!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeEndDate < formData.SchemeStartDate))}
                  helperText={!isViewMode && !formData.SchemeEndDate ? 'End Date is required' : (!isViewMode && !!formData.SchemeStartDate && !!formData.SchemeEndDate && formData.SchemeEndDate < formData.SchemeStartDate ? 'End Date cannot be before Start Date' : '')} />
              </Grid>

              {/* Supplier Search — select fills SupplierName, ContactNumber, Address */}
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={suppliers}
                  value={selectedSupplier}
                  onChange={handleSelectSupplier}
                  disabled={isViewMode}
                  getOptionLabel={(option) => option.Name || option.SupplierName || ''}
                  filterOptions={(options, { inputValue }) => {
                    const term = inputValue.trim().toLowerCase();
                    if (!term) return options;
                    return options.filter(s =>
                      (s.Name || '').toLowerCase().includes(term)
                    );
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ py: 0.5, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                        {option.Name || option.SupplierName || '-'}
                      </Typography>
                    </Box>
                  )}
                  freeSolo
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                      setFormData(prev => ({ ...prev, SupplierName: value, MasterId: null }));
                      setCustomerFound(false);
                      setSelectedSupplier(null);
                    }
                  }}
                  inputValue={formData.SupplierName || ''}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Supplier Name *"
                      placeholder="Search supplier..." sx={compactInput}
                      helperText={customerFound ? '✓ Selected' : ''}
                      FormHelperTextProps={{ sx: { color: '#059669', fontSize: '0.6rem', mt: 0.1 } }} />
                  )}
                  noOptionsText="Not found"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} disabled={isViewMode}
                  onChange={(e) => handleChange('Remarks', e.target.value)} sx={compactInput} placeholder="Enter remarks" />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Scheme Details Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af' }}>
                SCHEME ITEMS ({schemeDetails.length})
              </Typography>
              {!isViewMode && (
                <Button size="small" variant="contained" startIcon={<Add />} onClick={addDetailRow}
                  sx={{ fontSize: '0.7rem', py: 0.3, textTransform: 'none', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}>
                  Add Row
                </Button>
              )}
            </Box>

            {/* Detail validation error message */}
            {detailError && (
              <Typography sx={{ color: '#dc2626', fontSize: '0.75rem', mt: 0.5, mb: 0.5, fontWeight: 500 }}>
                {detailError}
              </Typography>
            )}

            {/* Details Table */}
            <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #1e3a6e, #2c4f8c)' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Target</th>
                    <th style={{ ...thStyle, minWidth: 150 }}>Brand</th>
                    <th style={{ ...thStyle, minWidth: 150 }}>Category</th>
                    <th style={{ ...thStyle, minWidth: 180 }}>Item</th>
                    <th style={thStyle}>Qty</th>
                    <th style={thStyle}>Rate</th>
                    <th style={{ ...thStyle, minWidth: 200 }}>Description</th>
                    {!isViewMode && <th style={thStyle}>×</th>}
                  </tr>
                </thead>
                <tbody>
                  {schemeDetails.length === 0 ? (
                    <tr>
                      <td colSpan={isViewMode ? 8 : 9} style={{ textAlign: 'center', padding: '16px', color: '#94a3b8' }}>
                        No details added. Click "Add Row" to begin.
                      </td>
                    </tr>
                  ) : (
                    schemeDetails.map((detail, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tdStyle}>{index + 1}</td>
                        <td style={tdStyle}>
                          <FormControl size="small" sx={{ minWidth: 70 }}>
                            <Select value={detail.IsTarget || 1} disabled={isViewMode}
                              onChange={(e) => handleDetailChange(index, 'IsTarget', e.target.value)}
                              sx={{ fontSize: '0.72rem', '& .MuiSelect-select': { py: 0.5, px: 1 } }}>
                              <MenuItem value={1} sx={{ fontSize: '0.75rem' }}>Qty</MenuItem>
                              <MenuItem value={2} sx={{ fontSize: '0.75rem' }}>Rate</MenuItem>
                            </Select>
                          </FormControl>
                        </td>
                        <td style={tdStyle}>
                          <Autocomplete
                            options={brands}
                            value={
                              brands.find(b => b.Id === detail.BrandId) ||
                              (detail.BrandId && detail.BrandName
                                ? { Id: detail.BrandId, Name: detail.BrandName }
                                : null)
                            }
                            onChange={(e, val) => handleDetailChange(index, 'BrandId', val ? val.Id : null)}
                            disabled={isViewMode}
                            getOptionLabel={(option) => option.Name || ''}
                            filterOptions={(options, { inputValue }) => {
                              const term = inputValue.toLowerCase();
                              return options.filter(b => (b.Name || '').toLowerCase().includes(term)).slice(0, 20);
                            }}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} sx={{ fontSize: '0.75rem', py: 0.3, px: 1.5 }}>
                                {option.Name}
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Search brand..."
                                sx={{ minWidth: 130, '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.4 }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }} />
                            )}
                            size="small"
                            noOptionsText="No brands"
                          />
                        </td>
                        <td style={tdStyle}>
                          <Autocomplete
                            options={categories}
                            value={
                              categories.find(c => c.Id === detail.CategoryId) ||
                              (detail.CategoryId && detail.CategoryName
                                ? { Id: detail.CategoryId, Name: detail.CategoryName }
                                : null)
                            }
                            onChange={(e, val) => handleDetailChange(index, 'CategoryId', val ? val.Id : null)}
                            disabled={isViewMode}
                            getOptionLabel={(option) => option.Name || ''}
                            filterOptions={(options, { inputValue }) => {
                              const term = inputValue.toLowerCase();
                              return options.filter(c => (c.Name || '').toLowerCase().includes(term)).slice(0, 20);
                            }}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} sx={{ fontSize: '0.75rem', py: 0.3, px: 1.5 }}>
                                {option.Name}
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Search category..."
                                sx={{ minWidth: 130, '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.4 }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }} />
                            )}
                            size="small"
                            noOptionsText="No categories"
                          />
                        </td>
                        <td style={tdStyle}>
                          <Autocomplete
                            options={rowItems[index] || []}
                            value={
                              (rowItems[index] || []).find(i => i.Id === detail.ItemId) ||
                              // Fallback: use NotMapped ItemName from API when rowItems not yet loaded
                              (detail.ItemId && detail.ItemName
                                ? { Id: detail.ItemId, Name: detail.ItemName, Code: null }
                                : null)
                            }
                            onChange={(e, val) => handleDetailChange(index, 'ItemId', val ? val.Id : null)}
                            disabled={isViewMode || (!detail.BrandId && !detail.CategoryId)}
                            loading={!!rowItemsLoading[index]}
                            getOptionLabel={(option) => option.Code ? `${option.Code} - ${option.Name}` : option.Name || ''}
                            filterOptions={(options, { inputValue }) => {
                              const term = inputValue.toLowerCase();
                              if (!term) return options;
                              return options.filter(i =>
                                (i.Name || '').toLowerCase().includes(term) ||
                                (i.Code || '').toLowerCase().includes(term)
                              );
                            }}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} sx={{ fontSize: '0.75rem', py: 0.3, px: 1.5 }}>
                                {option.Code ? `${option.Code} - ` : ''}{option.Name}
                              </Box>
                            )}
                            renderInput={(params) => (
                              <TextField {...params} size="small"
                                placeholder={!detail.BrandId && !detail.CategoryId ? 'Select brand/category first' : rowItemsLoading[index] ? 'Loading...' : 'Search item...'}
                                sx={{ minWidth: 160, '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.4 }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }} />
                            )}
                            size="small"
                            noOptionsText={rowItemsLoading[index] ? 'Loading...' : 'No items found'}
                          />
                        </td>
                        <td style={tdStyle}>
                          {detail.IsTarget === 1 ? (
                            <TextField size="small" value={detail.Quantity || ''} disabled={isViewMode}
                              onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)}
                              sx={tinyInput} style={{ width: 60 }} />
                          ) : (
                            <span style={{ fontSize: '0.7rem', color: '#94a3b8', padding: '0 8px' }}>-</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          {detail.IsTarget === 2 ? (
                            <TextField size="small" value={detail.Rate || ''} disabled={isViewMode}
                              onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                              sx={tinyInput} style={{ width: 70 }} />
                          ) : (
                            <span style={{ fontSize: '0.7rem', color: '#94a3b8', padding: '0 8px' }}>-</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <TextField size="small" value={detail.Description || ''} disabled={isViewMode}
                            onChange={(e) => handleDetailChange(index, 'Description', e.target.value)}
                            placeholder="Enter description..."
                            multiline maxRows={3}
                            sx={{ minWidth: 180, '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5, px: 0.8 }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                            style={{ width: '100%' }} />
                        </td>
                        {!isViewMode && (
                          <td style={tdStyle}>
                            <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ color: '#dc2626', p: 0.3 }}>
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseClick} size="small" variant="outlined" sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button onClick={() => {
              // Validate End Date
              if (!formData.SchemeEndDate) {
                setDetailError('Scheme End Date is required');
                return;
              }
              if (formData.SchemeStartDate && formData.SchemeEndDate && formData.SchemeEndDate < formData.SchemeStartDate) {
                setDetailError('End Date cannot be before Start Date');
                return;
              }
              // Validate all rows have item selected and qty/rate filled
              const invalidItemRow = schemeDetails.find(d => !d.ItemId);
              if (invalidItemRow) {
                setDetailError('Please select an item for all rows or remove empty rows');
                return;
              }
              const invalidQtyRow = schemeDetails.find(d => d.IsTarget === 1 && (!d.Quantity || parseFloat(d.Quantity) <= 0));
              if (invalidQtyRow) {
                setDetailError('Quantity is required for all items with Target type "Qty"');
                return;
              }
              const invalidRateRow = schemeDetails.find(d => d.IsTarget === 2 && (!d.Rate || parseFloat(d.Rate) <= 0));
              if (invalidRateRow) {
                setDetailError('Rate is required for all items with Target type "Rate"');
                return;
              }
              setDetailError('');
              onSave();
            }} size="small" variant="contained"
              sx={{ textTransform: 'none', fontSize: '0.8rem', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}>
              {modalAction === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation */}
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#d97706' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">You have unsaved changes. Are you sure you want to discard them?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} size="small">Stay</Button>
          <Button onClick={handleConfirmCancel} color="error" size="small" variant="contained">Discard</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SchemeModal;
